/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MonacoEditor from 'react-monaco-editor';
import { Loading } from 'carbon-addons-iot-react';
import { Resizable } from 're-resizable';
import { useField } from 'formik';
import { useDebounce } from 'use-debounce';
import merge from 'lodash/merge';
import noop from 'lodash/noop';

const propTypes = {
  /* width of resizable container */
  width: PropTypes.string,

  /* initial height of resizable container */
  height: PropTypes.string,

  fieldId: PropTypes.string.isRequired,
  formikModelToString: PropTypes.func,
  stringToFormikModel: PropTypes.func,

  /* MonacoEditor options passthru.
   * Only a small subset of options that actually use are specified here.
   * For the full set, see:
   * https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.istandaloneeditorconstructionoptions.html
   */
  options: PropTypes.shape({
    readOnly: PropTypes.bool,
    minimap: PropTypes.shape({
      enabled: PropTypes.bool,
    }),
    lineNumbers: PropTypes.bool,
    lineNumbersMinChars: PropTypes.number,
    selectOnLineNumbers: PropTypes.bool,
    folding: PropTypes.bool,
    foldingHighlight: PropTypes.bool,
  }),
  language: PropTypes.string.isRequired,
  theme: PropTypes.string,

  editorWillMount: PropTypes.func,
  editorDidMount: PropTypes.func,

  /**
   * onChange(text) - fires immediately after each change event
   * Used when we want immediate notification of updates, rather than waiting
   * for the debounced sync to formik to complete.
   */
  onChange: PropTypes.func,

  onChangeParseErrorStatus: PropTypes.func,

  /**
   * Milliseconds to wait since last input from user before syncing editor contents back to formik state
   */
  syncToFormikDebounceMillis: PropTypes.number,

  isLoading: PropTypes.bool,
};

const defaultProps = {
  width: '100%',
  height: '300px',

  options: {},

  theme: 'vs-light',

  formikModelToString: model => model,
  stringToFormikModel: string => string,

  editorWillMount: noop,
  editorDidMount: noop,

  onChange: noop,

  /**
   * Flag to indicate whether or not stringToFormikModel threw an when attempting to sync
   * the value of the text editor back to the Formik model.
   *
   * Null indicates that a a parse is currently in progress. I.e. set to null as soon
   * as the editor contents are changed, and only set to true/false once syncToFormikDebounceMillis has
   * expired and formikModelToString execution has completed
   *
   * This is typically passed in as the "sourceHasErrors" prop of the SourceViewSwitcher this editor is associated with
   * and is used by the SourceViewSwitcher to disable the "switch to graphical view" button
   * until formik sync has completed (successfully or not), and to show the "invalid source" model if the
   * model cannot be synced due to an error being thrown by stringToFormikModel.
   */
  onChangeParseErrorStatus: noop,

  syncToFormikDebounceMillis: 500,

  isLoading: false,
};

// Merged with supplied options parameter, giving precedence to supplied
// NOTE: we can't do this using defaultProps since that only does a shallow merge
const DEFAULT_OPTIONS = {
  minimap: {
    enabled: false,
  },
  lineNumbers: false,
  readOnly: true,
  scrollbar: {
    alwaysConsumeMouseWheel: false,
  },
  scrollBeyondLastLine: false,
};

const MonacoEditorFormik = ({
  width,
  height,
  fieldId,
  formikModelToString,
  stringToFormikModel,
  language,
  theme,
  options,
  syncToFormikDebounceMillis,
  editorWillMount,
  editorDidMount,
  onChange,
  onChangeParseErrorStatus,
  isLoading,
}) => {
  const [monaco, setMonaco] = useState(null);
  const [editor, setEditor] = useState(null);

  const [field, meta, helpers] = useField({
    name: fieldId,
  });

  const [value, setValue] = useState({
    // The contents of the input field
    data: formikModelToString(field.value),

    // iff true, the value of data will be syned back to Formik state
    // once the debounce timeout expires
    syncToFormik: false,
  });

  // periodically sync changes to value back to formik state
  // NOTE: we can't just sync to Formik on save or when leaving source view
  // since other components on the same page may rely on the formik model being up to date
  // (e.g. mappings editor uses formik state of event schemas to make autocomplete suggestions)
  const [valueDebounced] = useDebounce(value, syncToFormikDebounceMillis);
  useEffect(
    () => {
      if (!monaco || !editor || !helpers) {
        return;
      }
      if (valueDebounced.syncToFormik) {
        try {
          helpers.setValue(stringToFormikModel(valueDebounced?.data));
          onChangeParseErrorStatus(false);
        } catch (parseError) {
          // Suppress sync to formik if we are unable to turn the user's input into a Formik model
          // (e.g. this is a JSON editor and they've input invalid JSON)
          onChangeParseErrorStatus(true);
        }
      }
    },
    // syncToFormikWhenErrors, field.value, stringToFormikModel, helpers are all technically required, but including them
    // (i.e. the arrow functions) causes unnecessary re-renders/ ... not sure how to fix this - useCallback or something?
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monaco, editor, valueDebounced]
  );
  // sync changes made to formik model back to the rendered value
  // (this occurs e.g. when the form is reset, if the user selects a different event ID in the PI editor, if the user removes a row from the schema editor table, etc)
  useEffect(() => {
    setValue({
      data: formikModelToString(field.value),
      // here we are syncing an update from formik to our locally-rendered value
      // don't propagate this change back to Formik when the debounce timeout expires
      syncToFormik: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  // merge supplied options with some defauls, giving precedence to supplied
  // NOTE: we can't do this using defaultProps since that only does a shallow merge
  const mergedOptions = merge(DEFAULT_OPTIONS, options);

  // this is necessary to ensure readOnly gets reliably updated post-creation.
  useEffect(() => {
    if (editor) {
      editor.updateOptions({ readOnly: mergedOptions.readOnly });
    }
  }, [editor, mergedOptions.readOnly]);

  return (
    <Resizable
      defaultSize={{
        width,
        height,
      }}
      enable={{
        bottom: true,
        top: true,
        right: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      // ensure monaco editor size is updated accordingly as the user drags the resize handle
      onResize={() => {
        if (editor) {
          editor.layout();
        }
      }}>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            flexFlow: 'row nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}>
          <Loading active withOverlay={false} />
        </div>
      ) : (
        <MonacoEditor
          height="100%"
          width="100%"
          language={language}
          theme={theme}
          value={value?.data || ''}
          onChange={(newValue, __) => {
            onChangeParseErrorStatus(null);

            // update the rendered value
            setValue({
              data: newValue,
              syncToFormik: true,
            });

            if (onChange) {
              onChange(newValue);
            }

            // Update the touched status of the field to ensure reset works correctly
            // Formik runs validation whenever setTouched is called (if validateOnBlur=true, even if validateOnChange is false)
            // So we ensure this method is only called for the first change since the last reset
            if (!meta.touched) {
              helpers.setTouched(true);
            }
          }}
          options={mergedOptions}
          editorWillMount={m => {
            setMonaco(m);
            editorWillMount(m);
          }}
          editorDidMount={e => {
            setEditor(e);
            editorDidMount(e);
          }}
        />
      )}
    </Resizable>
  );
};

MonacoEditorFormik.propTypes = propTypes;
MonacoEditorFormik.defaultProps = defaultProps;

export default MonacoEditorFormik;
